import { useId, useState } from 'react';
import { ChevronDown } from 'assets/icons';

// A select-like field whose list opens below it, inline: an absolute dropdown
// would be cut by the modal's scrolling body. Texts come from the caller, so
// that each screen keeps its own wording.
const PerimeterChecklistField = ({
  label,
  perimeters,
  selectedIds,
  onChange,
  placeholder,
  emptyText,
  isDisabled = false,
  disabledText,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  const valueId = `${fieldId}-value`;
  const listId = `${fieldId}-list`;
  const isListShown = isOpen && !isDisabled;

  const selectedNames = perimeters
    .filter((perimeter) => selectedIds.includes(perimeter.id))
    .map((perimeter) => perimeter.name);
  const shownValue = isDisabled
    ? disabledText
    : selectedNames.join(', ') || placeholder;
  const isPlaceholder = isDisabled || selectedNames.length === 0;

  const togglePerimeter = (id) =>
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id],
    );

  return (
    <div className={'flex flex-col gap-1'}>
      <span id={labelId} className={'ft-sm text-gray-500'}>
        {label}
      </span>
      <button
        type={'button'}
        disabled={isDisabled}
        aria-labelledby={`${labelId} ${valueId}`}
        aria-expanded={isListShown}
        aria-controls={isListShown ? listId : undefined}
        onClick={() => setIsOpen((previous) => !previous)}
        className={`flex h-10 w-full items-center justify-between gap-2 rounded-md border px-3 text-left text-sm focus:border-yellow-500 focus:outline-none ${
          isDisabled ? 'cursor-not-allowed bg-gray-100' : 'bg-white'
        }`}
      >
        <span
          id={valueId}
          className={`min-w-0 truncate ${
            isPlaceholder ? 'text-gray-400' : 'text-gray-900'
          }`}
        >
          {shownValue}
        </span>
        <ChevronDown wh={20} color={isDisabled ? '#9CA3AF' : '#374151'} />
      </button>
      {isListShown ? (
        <div
          id={listId}
          role={'group'}
          aria-labelledby={labelId}
          className={
            'max-h-60 overflow-y-auto rounded-lg border border-gray-200'
          }
        >
          {perimeters.length === 0 ? (
            <p className={'px-4 py-6 text-center text-sm text-gray-500'}>
              {emptyText}
            </p>
          ) : (
            <ul className={'divide-y divide-gray-100'}>
              {perimeters.map((perimeter) => (
                <li key={perimeter.id}>
                  <label
                    className={
                      'flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-gray-50'
                    }
                  >
                    <input
                      type={'checkbox'}
                      checked={selectedIds.includes(perimeter.id)}
                      onChange={() => togglePerimeter(perimeter.id)}
                      className={'h-4 w-4 shrink-0 accent-yellow-600'}
                    />
                    <span className={'truncate text-sm text-gray-900'}>
                      {perimeter.name}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default PerimeterChecklistField;
